import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { EHidratacaoQueimadoType } from '@enums/HidratacaoQueimadoType';
import BigNumber from 'bignumber.js';

import { HidratacaoPediatriaContext } from './context';
import type { IArea } from './IUseHidratacaoPediatria';

export const HidratacaoPediatriaProvider = ({
  children,
}: PropsWithChildren) => {
  const [caloricWeight, setCaloricWeight] = useState<number>(0);
  const [dailyVolume, setDailyVolume] = useState<number>(0);
  const [dailyVolume50Percent, setDailyVolume50Percent] = useState<number>(0);
  const [KCl, setKCl] = useState<number>(2.5);
  const [KClCalculated, setKClCalculated] = useState<number>(0);
  const [mEq, setMEq] = useState<number>(136);
  const [NaCl, setNaCl] = useState<number>(3);
  const [NaClHypotonicSolution, setNaClHypotonicSolution] = useState<number>(0);
  const [NaClIsotonicSolution, setNaClIsotonicSolution] = useState<number>(0);
  const [weight, setWeight] = useState<number>(0);

  const [age, setAge] = useState<number>(0);
  const [areas, setAreas] = useState<IArea[]>([
    {
      adultPercentage: 4.5,
      childrenPercentage: 9,
      id: 1,
      isSelected: false,
      name: 'Cabeça - frente',
    },
    {
      adultPercentage: 4.5,
      childrenPercentage: 9,
      id: 2,
      isSelected: false,
      name: 'Cabeça - atrás',
    },
    {
      adultPercentage: 9,
      childrenPercentage: 9,
      id: 3,
      isSelected: false,
      name: 'Tórax - frente',
    },
    {
      adultPercentage: 9,
      childrenPercentage: 6.5,
      id: 4,
      isSelected: false,
      name: 'Tórax - atrás',
    },
    {
      adultPercentage: 9,
      childrenPercentage: 9,
      id: 5,
      isSelected: false,
      name: 'Abdome - frente',
    },
    {
      adultPercentage: 9,
      childrenPercentage: 6.5,
      id: 6,
      isSelected: false,
      name: 'Abdome - atrás',
    },
    {
      adultPercentage: 4.5,
      childrenPercentage: 4.5,
      id: 7,
      isSelected: false,
      name: 'MSD - frente',
    },
    {
      adultPercentage: 4.5,
      childrenPercentage: 4.5,
      id: 8,
      isSelected: false,
      name: 'MSD - atrás',
    },
    {
      adultPercentage: 4.5,
      childrenPercentage: 4.5,
      id: 9,
      isSelected: false,
      name: 'MSE - frente',
    },
    {
      adultPercentage: 4.5,
      childrenPercentage: 4.5,
      id: 10,
      isSelected: false,
      name: 'MSE - atrás',
    },
    {
      adultPercentage: 9,
      childrenPercentage: 7,
      id: 11,
      isSelected: false,
      name: 'MID - frente',
    },
    {
      adultPercentage: 9,
      childrenPercentage: 7,
      id: 12,
      isSelected: false,
      name: 'MID - atrás',
    },
    {
      adultPercentage: 9,
      childrenPercentage: 7,
      id: 13,
      isSelected: false,
      name: 'MIE - atrás',
    },
    {
      adultPercentage: 9,
      childrenPercentage: 7,
      id: 14,
      isSelected: false,
      name: 'MIE - atrás',
    },
    {
      adultPercentage: 1,
      childrenPercentage: 0,
      id: 15,
      isSelected: false,
      name: 'Genitália (adulto)',
    },
    {
      adultPercentage: 0,
      childrenPercentage: 2.5,
      id: 16,
      isSelected: false,
      name: 'Nádega D (criança)',
    },
    {
      adultPercentage: 0,
      childrenPercentage: 2.5,
      id: 17,
      isSelected: false,
      name: 'Nádega E (criança)',
    },
  ]);
  const [type, setType] = useState<EHidratacaoQueimadoType>();
  const [SCQTotal, setSCQTotal] = useState<number>(0);

  const calculateDailyVolume = useCallback((): void => {
    const weightParsed = new BigNumber(weight);
    if (weight < 1.5) {
      // FÓRMULA: PESO * 140
      const result100Percent = weightParsed.multipliedBy(140);
      const result50Percent = result100Percent.dividedBy(2);
      setDailyVolume(result100Percent.toNumber() || 0);
      setDailyVolume50Percent(result50Percent.toNumber() || 0);
      return;
    }
    if (weight >= 1.5 && weight <= 2) {
      // FÓRMULA: PESO * 120
      const result100Percent = weightParsed.multipliedBy(120);
      const result50Percent = result100Percent.dividedBy(2);
      setDailyVolume(result100Percent.toNumber() || 0);
      setDailyVolume50Percent(result50Percent.toNumber() || 0);
      return;
    }
    if (weight > 2 && weight <= 10) {
      // FÓRMULA: PESO * 100
      const result100Percent = weightParsed.multipliedBy(100);
      const result50Percent = result100Percent.dividedBy(2);
      setDailyVolume(result100Percent.toNumber() || 0);
      setDailyVolume50Percent(result50Percent.toNumber() || 0);
      return;
    }
    if (weight > 10 && weight <= 20) {
      // FÓRMULA: ((PESO - 10) * 50) + 1000
      const step1 = weightParsed.minus(10);
      const step2 = step1.multipliedBy(50);
      const result100Percent = step2.plus(1000);
      const result50Percent = result100Percent.dividedBy(2);
      setDailyVolume(result100Percent.toNumber() || 0);
      setDailyVolume50Percent(result50Percent.toNumber() || 0);
      return;
    }
    if (weight > 20 && weight <= 40) {
      // FÓRMULA: ((PESO - 20) * 20) + 1000 + 500
      const step1 = weightParsed.minus(20);
      const step2 = step1.multipliedBy(20);
      const step3 = step2.plus(1000);
      const result100Percent = step3.plus(500);
      const result50Percent = result100Percent.dividedBy(2);
      setDailyVolume(result100Percent.toNumber() || 0);
      setDailyVolume50Percent(result50Percent.toNumber() || 0);
      return;
    }
    // FÓRMULA: 2400
    setDailyVolume(2400);
    setDailyVolume50Percent(1200);
  }, [weight]);

  useEffect(() => {
    calculateDailyVolume();
  }, [calculateDailyVolume]);

  const calculateCaloricWeight = useCallback((): void => {
    const dailyVolumeParsed = new BigNumber(dailyVolume);
    const result = dailyVolumeParsed.dividedBy(100);
    setCaloricWeight(result.toNumber() || 0);
  }, [dailyVolume]);

  useEffect(() => {
    calculateCaloricWeight();
  }, [calculateCaloricWeight]);

  const calculateNaClHypotonicSolution = useCallback((): void => {
    // FÓRMULA: ((PESO CALÓRICO * NaCl/dia) / 3,4) / 2
    const caloricWeightParsed = new BigNumber(caloricWeight);
    const NaClParsed = new BigNumber(NaCl);
    const step1 = caloricWeightParsed.multipliedBy(NaClParsed);
    const step2 = step1.dividedBy(3.4);
    const result = step2.dividedBy(2);
    setNaClHypotonicSolution(result.toNumber() || 0);
  }, [NaCl, caloricWeight]);

  useEffect(() => {
    calculateNaClHypotonicSolution();
  }, [calculateNaClHypotonicSolution]);

  const calculateKClHypotonicSolution = useCallback((): void => {
    // FÓRMULA: ((PESO CALÓRICO * KCl/dia) / 2,5) / 2
    const caloricWeightParsed = new BigNumber(caloricWeight);
    const KClParsed = new BigNumber(KCl);
    const step1 = caloricWeightParsed.multipliedBy(KClParsed);
    const step2 = step1.dividedBy(2.5);
    const result = step2.dividedBy(2);
    setKClCalculated(result.toNumber() || 0);
  }, [KCl, caloricWeight]);

  useEffect(() => {
    calculateKClHypotonicSolution();
  }, [calculateKClHypotonicSolution]);

  const calculateNaClIsotonicSolution = useCallback((): void => {
    // FÓRMULA: (((VOLUME DIÁRIO DE SG 5% / 2) * mEq/L) / 1000) / 3,4
    const dailyVolume50PercentParsed = new BigNumber(dailyVolume50Percent);
    const mEqParsed = new BigNumber(mEq);
    const step1 = dailyVolume50PercentParsed.multipliedBy(mEqParsed);
    const step2 = step1.dividedBy(1000);
    const result = step2.dividedBy(3.4);
    setNaClIsotonicSolution(result.toNumber() || 0);
  }, [dailyVolume50Percent, mEq]);

  useEffect(() => {
    calculateNaClIsotonicSolution();
  }, [calculateNaClIsotonicSolution]);

  const handleKCl = useCallback((value: number): void => {
    setKCl(value);
  }, []);

  const handleMEq = useCallback((value: number): void => {
    setMEq(value);
  }, []);

  const handleNaCl = useCallback((value: number): void => {
    setNaCl(value);
  }, []);

  const handleWeight = useCallback((value: number): void => {
    setWeight(value);
  }, []);

  //

  const handleAge = useCallback(
    (value: number): void => {
      setAge(value);
      const SCQCalculated = areas.reduce((total, item) => {
        if (!item.isSelected) {
          return total + 0;
        }
        if (value >= 14) {
          return total + item.adultPercentage;
        }
        return total + item.childrenPercentage;
      }, 0);
      setSCQTotal(SCQCalculated);
    },
    [areas],
  );

  const handleType = useCallback((value: EHidratacaoQueimadoType): void => {
    setType(value);
  }, []);

  const handleSelectChangeArea = useCallback(
    (area: IArea) => {
      setAreas((current) => {
        const newAreaList = current.map((item) => {
          if (item.id === area.id) {
            return {
              ...item,
              isSelected: !item.isSelected,
            };
          }
          return item;
        });
        const SCQCalculated = newAreaList.reduce((total, item) => {
          if (!item.isSelected) {
            return total + 0;
          }
          if (age >= 14) {
            return total + item.adultPercentage;
          }
          return total + item.childrenPercentage;
        }, 0);
        setSCQTotal(SCQCalculated);
        return newAreaList;
      });
    },
    [age],
  );

  const handleResetSCQ = useCallback((): void => {
    setSCQTotal(0);
  }, []);

  const handleSCQ = useCallback((value: number): void => {
    setSCQTotal(value);
  }, []);

  return (
    <HidratacaoPediatriaContext.Provider
      value={{
        age,
        areas,
        caloricWeight,
        changeAreaSelected: handleSelectChangeArea,
        dailyVolume,
        dailyVolume50Percent,
        KCl,
        KClCalculated,
        mEq,
        NaCl,
        NaClHypotonicSolution,
        resetSCQ: handleResetSCQ,
        SCQTotal,
        setAge: handleAge,
        setKCl: handleKCl,
        setMEq: handleMEq,
        setNaCl: handleNaCl,
        setSCQ: handleSCQ,
        setType: handleType,
        setWeight: handleWeight,
        type: type!,
        weight,
        NaClIsotonicSolution,
      }}>
      {children}
    </HidratacaoPediatriaContext.Provider>
  );
};
